import { lazy, Suspense, useContext } from "react";
import { Routes, Route } from "react-router-dom";
import { useSelector } from "react-redux";

import AuthContext from "./store/context/auth-context";

import useAppInit from "./hooks/use-app-init";

import Layout from "./layouts/main";
import ClientLayout from "./layouts/clients";

import ProtectedRoute from "./components/routes/ProtectedRoute";
import PublicRoute from "./components/routes/PublicRoute";
import PageLoader from "./components/common/PageLoader";

//  PUBLIC PAGES (LAZY)
const LandingPage = lazy(() => import("./pages/Landing"));
const Login = lazy(() => import("./pages/Login"));
const Register = lazy(() => import("./pages/Register"));
const Forget = lazy(() => import("./pages/Forget"));
const OtpVerificationPage = lazy(() => import("./pages/OtpVerificationPage"));

const ProductDetailPage = lazy(() => import("./pages/ProductDetail"));
const CollectionPage = lazy(() => import("./pages/Collection"));

const CartPage = lazy(() => import("./pages/Cart"));

const CheckoutPage = lazy(() => import("./pages/Checkout"));

const PurchaseSuccessPage = lazy(() => import("./pages/PurchaseSuccess"));

const CategoryPage = lazy(() => import("./pages/Category"));
const SearchPage = lazy(() => import("./pages/Search"));

const PrivacyPolicyPage = lazy(() => import("./pages/PrivacyPolicy"));

const TermsOfUsePage = lazy(() => import("./pages/TermsOfUse"));

const NotFound = lazy(() => import("./pages/NotFound"));

//  CLIENT PAGES (LAZY)
const ManageAccountPage = lazy(() => import("./pages/client/ManageAccount"));

const MyProfilePage = lazy(() => import("./pages/client/MyProfile"));

const MyProfileEditPage = lazy(() => import("./pages/client/MyProfileEdit"));

const AddressBookPage = lazy(() => import("./pages/client/AddressBook"));

const MyCancellationsPage = lazy(
  () => import("./pages/client/MyCancellations"),
);

const MyReturnsPage = lazy(() => import("./pages/client/MyReturns"));

const MyOrderPage = lazy(() => import("./pages/client/MyOrder"));

const MyOrderDetailPage = lazy(() => import("./pages/client/MyOrderDetail"));

//  VENDOR PAGES (LAZY)
const ShopPage = lazy(() => import("./pages/vendor/Shop"));

const ShopAllProducts = lazy(
  () =>
    import("./features/shop/components/shopProducts/components/ShopAllProducts"),
);

const ShopCatProducts = lazy(
  () =>
    import("./features/shop/components/shopProducts/components/ShopCatProducts"),
);

function App() {
  useAppInit();

  const authCtx = useContext(AuthContext);

  const isLoggedIn = authCtx.isLoggedIn;

  const checkoutItems = useSelector((state) => state.checkout.checkoutItems);

  const checkoutHasItems = checkoutItems.length > 0;

  return (
    <Layout>
      <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* ================= PUBLIC ROUTES ================= */}
          <Route path="/" element={<LandingPage />} />

          <Route path="/forget" element={<Forget />} />

          <Route
            path="/product/:categoryId/:productId"
            element={<ProductDetailPage />}
          />
          <Route
            path="/collection/:collectionSlug"
            element={<CollectionPage />}
          />

          <Route path="/category/:categoryName" element={<CategoryPage />} />

          <Route path="/cart" element={<CartPage />} />

          <Route path="/search" element={<SearchPage />} />

          <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />

          <Route path="/terms-of-use" element={<TermsOfUsePage />} />

          {/* ================= AUTH ONLY ================= */}
          <Route element={<PublicRoute isAllowed={!isLoggedIn} />}>
            <Route path="/login" element={<Login />} />

            <Route path="/register" element={<Register />} />
            <Route path="/verify-otp" element={<OtpVerificationPage />} />
          </Route>

          {/* ================= CHECKOUT ================= */}
          <Route
            element={
              <ProtectedRoute
                isAllowed={isLoggedIn && checkoutHasItems}
                redirectPath="/"
              />
            }
          >
            <Route path="/checkout" element={<CheckoutPage />} />
          </Route>

          {/* ================= SUCCESS ================= */}
          <Route element={<ProtectedRoute isAllowed={isLoggedIn} />}>
            <Route
              path="/checkout/success_checkout"
              element={<PurchaseSuccessPage />}
            />
          </Route>

          {/* ================= PROFILE ================= */}
          <Route element={<ProtectedRoute isAllowed={isLoggedIn} />}>
            <Route path="/profile" element={<ClientLayout />}>
              <Route index element={<ManageAccountPage />} />

              <Route path="userinfo">
                <Route index element={<MyProfilePage />} />

                <Route path="edit" element={<MyProfileEditPage />} />
              </Route>

              <Route path="address" element={<AddressBookPage />} />

              <Route path="my_orders">
                <Route index element={<MyOrderPage />} />

                <Route
                  path="order_detail/:orderDetailId"
                  element={<MyOrderDetailPage />}
                />
              </Route>

              <Route
                path="order_cancellations"
                element={<MyCancellationsPage />}
              />

              <Route path="order_returns" element={<MyReturnsPage />} />
            </Route>
          </Route>

          {/* ================= VENDOR ================= */}
          <Route path="/shop-collection/:shopNumber" element={<ShopPage />}>
            <Route index element={<ShopAllProducts />} />

            <Route
              path="category/:categoryName"
              element={<ShopCatProducts />}
            />
          </Route>

          {/* ================= 404 ================= */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </Layout>
  );
}

export default App;
